import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { daysheetAPI } from '../services/api';
import './Reports.css';

const Icon = ({ name, size = 18 }) => {
  const icons = {
    arrowLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>,
    trending: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-3-3.87"></path><path d="M9 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    fileText: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>,
  };
  return icons[name] || null;
};

const Reports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('attendance');
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [daysheetReport, setDaysheetReport] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadReports();
  }, [dateRange]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const [attendanceRes, employeeRes, daysheetRes] = await Promise.all([
        api.get('/hr/dashboard/attendance'),
        api.get('/hr/dashboard/employees'),
        daysheetAPI.getReport(dateRange)
      ]);
      setAttendanceData(attendanceRes.data.data || []);
      setEmployeeData(employeeRes.data.data || []);
      setDaysheetReport(daysheetRes.data.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateWorkHours = (punchIn, punchOut) => {
    if (!punchOut) return 'In Progress';
    const diff = new Date(punchOut) - new Date(punchIn);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <Layout>
      <div className="reports-analytics-page">
        <header className="page-header-compact">
          <div className="title-section">
            <button className="back-minimal" onClick={() => navigate('/dashboard')}>
              <Icon name="arrowLeft" size={16} />
              <span>Back</span>
            </button>
            <div className="text-wrapper">
              <h1>Reports & Analytics</h1>
              <p>Operational insights and productivity metrics</p>
            </div>
          </div>
          <div className="header-actions">
            <div className="compact-filter-bar">
              <div className="inline-date-group">
                <div className="field">
                  <label>From</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  />
                </div>
                <div className="divider"></div>
                <div className="field">
                  <label>To</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        </header>

        <nav className="tab-segmented-control">
          <button
            className={`seg-btn ${activeTab === 'attendance' ? 'active' : ''}`}
            onClick={() => setActiveTab('attendance')}
          >
            Attendance
          </button>
          <button
            className={`seg-btn ${activeTab === 'daysheet' ? 'active' : ''}`}
            onClick={() => setActiveTab('daysheet')}
          >
            Daysheet Analysis
          </button>
          <button
            className={`seg-btn ${activeTab === 'employee' ? 'active' : ''}`}
            onClick={() => setActiveTab('employee')}
          >
            Staff Directory
          </button>
        </nav>

        {loading ? (
          <div className="loading-state-minimal">
            <div className="marsh-spinner"></div>
            <p>Gathering insights...</p>
          </div>
        ) : (
          <main className="content-area-marsh">
            {activeTab === 'attendance' && (
              <div className="analytics-view">
                <div className="insight-row">
                  <div className="kpi-card">
                    <div className="kpi-icon blue"><Icon name="users" /></div>
                    <div className="kpi-data">
                      <label>Present Today</label>
                      <span className="value">{attendanceData.length}</span>
                    </div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-icon green"><Icon name="trending" /></div>
                    <div className="kpi-data">
                      <label>Daily Activity</label>
                      <span className="value">{Math.round((attendanceData.length / (employeeData.length || 1)) * 100)}%</span>
                    </div>
                  </div>
                </div>

                <div className="data-table-card">
                  <div className="card-header-minimal">
                    <h3>Today's Attendance Logs</h3>
                    <span className="badge-light">{attendanceData.length} records</span>
                  </div>
                  {attendanceData.length === 0 ? (
                    <div className="empty-state-minimal">
                      <Icon name="calendar" size={48} />
                      <h4>No Activity Logged</h4>
                      <p>Employees haven't started their work day yet.</p>
                    </div>
                  ) : (
                    <div className="table-wrapper-marsh">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>Role</th>
                            <th>Punch In</th>
                            <th>Punch Out</th>
                            <th>Duration</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attendanceData.map((record) => (
                            <tr key={record._id}>
                              <td>
                                <div className="user-profile-cell">
                                  <div className="avatar-pill">{record.user?.name?.charAt(0)}</div>
                                  <span className="name-bold">{record.user?.name}</span>
                                </div>
                              </td>
                              <td>{record.user?.role}</td>
                              <td>{formatTime(record.punchInTime)}</td>
                              <td>{formatTime(record.punchOutTime)}</td>
                              <td className="hours-val">{calculateWorkHours(record.punchInTime, record.punchOutTime)}</td>
                              <td>
                                <span className={`status-tag ${record.punchOutTime ? 'completed' : 'active'}`}>
                                  {record.punchOutTime ? 'Completed' : 'On Shift'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'daysheet' && daysheetReport && (
              <div className="analytics-view">
                <div className="insight-row">
                  <div className="kpi-card">
                    <div className="kpi-icon purple"><Icon name="fileText" /></div>
                    <div className="kpi-data">
                      <label>Total Submissions</label>
                      <span className="value">{daysheetReport.summary.submittedCount}</span>
                    </div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-icon blue"><Icon name="clock" /></div>
                    <div className="kpi-data">
                      <label>Avg. Daily Hours</label>
                      <span className="value">{daysheetReport.summary.avgWorkHours}h</span>
                    </div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-icon orange"><Icon name="alert" /></div>
                    <div className="kpi-data">
                      <label>Reported Blockers</label>
                      <span className="value">{daysheetReport.summary.withBlockers}</span>
                    </div>
                  </div>
                </div>

                <div className="data-table-card">
                  <div className="card-header-minimal">
                    <h3>Detailed Activity Log</h3>
                    <span className="badge-light">{daysheetReport.daysheets.length} entries</span>
                  </div>
                  <div className="table-wrapper-marsh">
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Employee</th>
                          <th>Hours</th>
                          <th>Location</th>
                          <th>Summary</th>
                          <th>Issues</th>
                        </tr>
                      </thead>
                      <tbody>
                        {daysheetReport.daysheets.length === 0 ? (
                          <tr className="no-hover"><td colSpan="6" className="empty-cell">No entries for this period</td></tr>
                        ) : (
                          daysheetReport.daysheets.map((ds) => (
                            <tr key={ds._id}>
                              <td className="date-cell">{formatDate(ds.date)}</td>
                              <td className="name-bold">{ds.user?.name}</td>
                              <td className="hours-val">{ds.totalWorkingHours.toFixed(1)}h</td>
                              <td>{ds.workLocation}</td>
                              <td className="summary-cell" title={ds.workSummary}>
                                {ds.workSummary.substring(0, 45)}...
                              </td>
                              <td>
                                {ds.blockers ? (
                                  <span className="issue-dot-active">Blocked</span>
                                ) : <span className="text-muted">None</span>}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'employee' && (
              <div className="analytics-view">
                <div className="data-table-card">
                  <div className="card-header-minimal">
                    <h3>Staff Directory</h3>
                    <span className="badge-light">{employeeData.length} active staff members</span>
                  </div>
                  <div className="table-wrapper-marsh">
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th>Full Name</th>
                          <th>Role</th>
                          <th>Contact</th>
                          <th>Manager</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeData.map((emp) => (
                          <tr key={emp._id}>
                            <td className="name-bold">{emp.name}</td>
                            <td><span className="role-pill">{emp.role}</span></td>
                            <td className="contact-text">{emp.mobile}</td>
                            <td>{emp.reportsTo?.name || <span className="text-muted">None</span>}</td>
                            <td>
                              <span className={`status-dot ${emp.isActive ? 'active' : 'idle'}`}>
                                {emp.isActive ? 'Active' : 'Offline'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </main>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
